import { Box, Grid, Paper, useTheme } from "@mui/material";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { Permissions, Precinct } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import type { AppColumn } from "../../components/tables/AppTable.types";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import PrecinctsContext from "../../contexts/PrecinctsContext";
import usePrecincts from "../../hooks/data/usePrecincts";

function requiredPrecinctId(row: Precinct): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Precinct is missing its ID.");
    }

    return row.id;
}

const usePrecinctColumns = () => {
    const agentColumns = useMemo(() => {
        const columns: AppColumn<Precinct>[] = [
            {
                id: "name",
                label: "Name",
                scalarType: "text",
                accessor: (row) => row.name,
                flex: 1,
            },
            {
                id: "rank",
                label: "Rank",
                scalarType: "number",
                accessor: (row) => row.rank,
                flex: 1,
            },
            {
                id: "actions",
                label: "",
                headerLabel: "",
                kind: "action",
                scalarType: "text",
                accessor: (row) => String(row.id ?? ""),
                width: 100,
                cell: ({ row }) => (
                    <GridActions
                        editPermission={Permissions.AdminAccess}
                        editUrl={`/admin/stage-precincts/edit/${requiredPrecinctId(row)}`}
                    />
                ),
            },
        ];

        return columns;
    }, []);

    return agentColumns;
};

const Precincts: FC = () => {
    const columns = usePrecinctColumns();

    const theme = useTheme();

    const context = usePrecincts();

    const { data: precincts, loading: precinctsLoading } = context;

    const exportMetadata = useMemo(
        () => ({
            fileName: `Stage Precincts`,
            exportTitle: `Stage Precincts`,
            includeDateTime: true,
        }),
        [],
    );

    return (
        <PrecinctsContext.Provider value={context}>
            <Outlet />
            <SetLinks createProjectLink={DefaultProjectCreateLink} stageLinkEnabled={false} lotLinkEnabled={false} />
            <Grid container gap={theme.spacing(2)} justifyContent={"end"} marginBottom={theme.spacing(2)}>
                <StyledButton
                    variant="contained"
                    //@ts-expect-error
                    component={Link}
                    to={`/admin/stage-precincts/create`}
                    state={{
                        back: true,
                    }}
                >
                    Add Precinct
                </StyledButton>
            </Grid>
            <Paper>
                <Box>
                    <CollapsibleAppTable
                        density="compact"
                        startCollapsed={false}
                        title="Precincts"
                        loading={precinctsLoading}
                        rows={precincts ?? []}
                        columns={columns}
                        getRowId={requiredPrecinctId}
                        columnControls={{
                            menu: true,
                        }}
                        filterControls
                        export={exportMetadata}
                    />
                </Box>
            </Paper>
        </PrecinctsContext.Provider>
    );
};

export default Precincts;
